import { Skeleton } from "@mantine/core";
import { Link } from "@tanstack/react-router";
import { Package, Plus } from "lucide-react";

import { useListProducts } from "../../../api/generated/endpoints/teams/teams";
import type { Product } from "../../../api/generated/model";
import { freshUrls, soonestExpiry } from "../../../api/signed-urls";
import { ErrorAlert } from "../../../platform/ErrorAlert";
import { useMembership } from "../../../platform/membership";
import { managesBrands } from "../../../platform/permissions";
import { CardPicture } from "../../../ui/CardPicture";
import { useThumbFirst } from "../../../ui/thumb-first";
import library from "../library/library.module.css";
import home from "../pro-home.module.css";
import { coverOf } from "../products/cover";
import { SoonControl } from "../Soon";
import classes from "./simple-sections.module.css";

/** The prototype's line under a product: its photos, and a nudge while it has fewer than four. */
function photosLine(count: number): string {
  const photos = count === 1 ? "1 photo" : `${count} photos`;
  return count < 4 ? `${photos} · add someone using it for better ads` : photos;
}

/**
 * A product's cover: its thumbnail first, the full picture when that fails, and the product's icon
 * over a full picture that fails too, kept mounted for the media cookies' retry until it loads
 * (`CardPicture`; the #1756 review).
 */
function ProductCover({ cover, onLost }: { cover: { full: string; thumb: string | null }; onLost: () => void }) {
  const picture = useThumbFirst(cover.thumb, cover.full, onLost);
  return (
    <CardPicture
      src={picture.src}
      alt=""
      onFailed={picture.onError}
      fallback={<Package size={28} strokeWidth={1.5} aria-hidden />}
    />
  );
}

interface ProductCardProps {
  teamId: string;
  brandId: string;
  product: Product;
  onLost: () => void;
}

function ProductCard({ teamId, brandId, product, onLost }: ProductCardProps) {
  const cover = coverOf(product);
  return (
    <article className={classes.product} aria-label={product.name}>
      <span className={classes.productArt}>
        {cover === null ? (
          <Package size={28} strokeWidth={1.5} aria-hidden />
        ) : (
          <ProductCover cover={cover} onLost={onLost} />
        )}
      </span>
      <b className={classes.tileName}>{product.name}</b>
      <span className={classes.tileLine}>{photosLine(product.images.length)}</span>
      <span className={classes.productActions}>
        {/* The UGC composer takes no product from a link yet: its own PR (spec ui-perf/simple-brand). */}
        <SoonControl className={home.smallButton} label={`Make an ad with ${product.name}`}>
          Make an ad
        </SoonControl>
        <Link
          to="/teams/$teamId/brands/$brandId/simple/products/$productId"
          params={{ teamId, brandId, productId: product.id }}
          className={home.smallButton}
          aria-label={`Details of ${product.name}`}
        >
          Details
        </Link>
      </span>
    </article>
  );
}

/**
 * Brand's Products (spec ui-perf/simple-brand): the brand's products on Pro's Products page's own
 * read (newest first, its key), read again before their covers' URLs expire; a product opens its
 * page in Simple, and an admin adds one from a link.
 */
export function SimpleProducts({ teamId, brandId }: { teamId: string; brandId: string }) {
  const me = useMembership(teamId);
  const products = useListProducts(
    teamId,
    brandId,
    { order: "newest" },
    { query: freshUrls((list: Product[]) => soonestExpiry(list.map((each) => each.urls_expire_at))) },
  );
  const add =
    me !== undefined && managesBrands(me.role) ? (
      <Link
        to="/teams/$teamId/brands/$brandId/simple/products/new"
        params={{ teamId, brandId }}
        className={library.primary}
      >
        <Plus size={14} aria-hidden /> Add product from a link
      </Link>
    ) : null;
  return (
    <section aria-label="Products">
      <div className={classes.productsHead}>{add}</div>
      {products.isError ? <ErrorAlert error={products.error} /> : null}
      {products.data === undefined ? (
        products.isError ? null : (
          <div className={classes.products}>
            {[0, 1, 2].map((each) => (
              <Skeleton key={each} className={home.mediaSkeleton} />
            ))}
          </div>
        )
      ) : products.data.length === 0 ? (
        <p className={classes.empty}>No products yet. Set one up once, and every tool can use it.</p>
      ) : (
        <div className={classes.products}>
          {products.data.map((product) => (
            <ProductCard
              key={product.id}
              teamId={teamId}
              brandId={brandId}
              product={product}
              onLost={() => void products.refetch()}
            />
          ))}
        </div>
      )}
    </section>
  );
}
